/**
 * Centralized Route Configuration
 * Maps route enums to components and metadata
 */

import { ComponentType, LazyExoticComponent } from 'react'
import { GamerRoutes, FacilitatorRoutes, SharedRoutes, PublicRoutes, SprintCallQueryParam } from './routeEnums'
import { OrgRole } from '../../../../pure-js/types/OrganizationMembership'
import { PARTICIPANT_ORG_ROLES, STAFF_ORG_ROLES } from '../../../../pure-js/libs/organizationMembershipHelpers'

/**
 * Route type classification
 */
export type RouteType = 'public' | 'shared' | 'facilitator' | 'gamer'

/**
 * Route metadata
 */
export interface RouteMetadata {
  path: string
  component?: ComponentType | LazyExoticComponent<ComponentType>
  authRequired: boolean
  routeType: RouteType
  allowedRoles?: OrgRole[]
  adminOnly?: boolean
  title?: string
}

const GAMER_ONLY_ROLES: RouteMetadata['allowedRoles'] = [OrgRole.GAMER]
const LEADER_ONLY_ROLES: RouteMetadata['allowedRoles'] = [OrgRole.LEADER]
const PARTICIPANT_ROLES: RouteMetadata['allowedRoles'] = [...PARTICIPANT_ORG_ROLES]
const GAMER_DEV_TOOLS_TEST_ROLES: RouteMetadata['allowedRoles'] = [...PARTICIPANT_ORG_ROLES, OrgRole.ADMIN]
const FACILITATOR_ROLES: RouteMetadata['allowedRoles'] = [OrgRole.FACILITATOR, OrgRole.ADMIN]
const SHARED_ROLES: RouteMetadata['allowedRoles'] = [...PARTICIPANT_ORG_ROLES, ...STAFF_ORG_ROLES]

/**
 * Gamer route paths mapped to their section identifiers
 * Used by dashboard to determine which component to render
 */
export const GAMER_ROUTE_TO_SECTION: Record<string, string> = {
  [GamerRoutes.BASE]: 'dashboard-gamer', // Base /gamer route
  [GamerRoutes.DASHBOARD]: 'dashboard-gamer',
  [GamerRoutes.MY_PEOPLE]: 'my-people',
  [GamerRoutes.LATEST_SESSION_FEEDBACK]: 'latest-session-feedback',
  [GamerRoutes.UPCOMING_ACTIVITIES]: 'upcoming-activities',
  [GamerRoutes.ALL_UPCOMING_ACTIVITIES]: 'all-upcoming-activities',
  [GamerRoutes.ONBOARDING]: 'onboarding',
  [GamerRoutes.MY_PROFILE]: 'my-profile',
  [GamerRoutes.ACCOUNT_SETTINGS]: 'account-settings',
  [GamerRoutes.PERMISSIONS_INSTRUCTIONS]: 'permissions-instructions',
  [GamerRoutes.FAQ]: 'faq',
  [GamerRoutes.TUTORIAL_VIDEOS]: 'tutorial-videos',
  [GamerRoutes.DEEPER_TEAM_ANALYSIS]: 'deeper-team-analysis',
  [GamerRoutes.DEEPER_INDIVIDUAL_ANALYSIS]: 'deeper-individual-analysis',
  [GamerRoutes.ACHIEVEMENTS]: 'achievements',
  [GamerRoutes.UNSEEN_ACHIEVEMENTS]: 'unseen-achievements',
  [GamerRoutes.AI_COACH]: 'ai-coach',
  [GamerRoutes.SPRINT_CALL]: 'sprint-call',
  [GamerRoutes.ARIA_COACH]: 'aria-coach',
  // Dev tools routes
  [GamerRoutes.DEV_TOOLS]: 'dev-tools',
  [GamerRoutes.DEV_TOOLS_VOICE_LOTTIE]: 'dev-tools-voice-lottie',
  [GamerRoutes.DEV_TOOLS_BUBBLE_LOTTIE]: 'dev-tools-bubble-lottie',
  [GamerRoutes.DEV_TOOLS_VOICE_ANIMATION_AUDIO]: 'dev-tools-voice-animation-audio',
  [GamerRoutes.DEV_TOOLS_RECHARTS_GLASS]: 'dev-tools-recharts-glass',
  [GamerRoutes.DEV_TOOLS_COMMUNICATION_FEELING]: 'dev-tools-communication-feeling',
  [GamerRoutes.DEV_TOOLS_STACKED_CHARTS]: 'dev-tools-stacked-charts',
  [GamerRoutes.DEV_TOOLS_COMPARISON_BAR]: 'dev-tools-comparison-bar',
  [GamerRoutes.DEV_TOOLS_TEAM_HEALTH_HEART]: 'dev-tools-team-health-heart',
  [GamerRoutes.DEV_TOOLS_GLINT_REVEAL]: 'dev-tools-glint-reveal',
  [GamerRoutes.DEV_TOOLS_GENERATE_REPORT_VISUALS]: 'dev-tools-generate-report-visuals',
  [GamerRoutes.DEV_TOOLS_FUNNEL_GRAPH]: 'dev-tools-funnel-graph',
  [GamerRoutes.DEV_TOOLS_FUNNEL_GRAPH_V2]: 'dev-tools-funnel-graph-v2',
  [GamerRoutes.DEV_TOOLS_FUNNEL_GRAPH_V3]: 'dev-tools-funnel-graph-v3',
  [GamerRoutes.DEV_TOOLS_TEAM_INFORMATION_SHARES]: 'dev-tools-team-information-shares',
  [GamerRoutes.DEV_TOOLS_ACHIEVEMENTS]: 'dev-tools-achievements',
  [GamerRoutes.DEV_TOOLS_PERSONALITY_TRAITS]: 'dev-tools-personality-traits',
  [GamerRoutes.DEV_TOOLS_BO_CLEANUP]: 'dev-tools-bo-cleanup'
}

/**
 * Facilitator route paths mapped to their section identifiers
 */
export const FACILITATOR_ROUTE_TO_SECTION: Record<string, string> = {
  [FacilitatorRoutes.BASE]: 'dashboard-facilitator', // Base /facilitator route
  [FacilitatorRoutes.DASHBOARD]: 'dashboard-facilitator',
  [FacilitatorRoutes.TRAINING_SPRINT_MANAGEMENT]: 'training-sprint-management',
  [FacilitatorRoutes.SPRINT_CREATION]: 'sprint-creation',
  [FacilitatorRoutes.SESSIONS]: 'sessions',
  [FacilitatorRoutes.UPCOMING_ACTIVITIES]: 'upcoming-activities',
  [FacilitatorRoutes.MANAGE_REPORTS]: 'manage-reports',
  [FacilitatorRoutes.GENERATE_REPORT]: 'generate-report',
  [FacilitatorRoutes.MANAGE_REFLECTIONS]: 'manage-reflections',
  [FacilitatorRoutes.FACILITATE_REFLECTION]: 'facilitate-reflection',
  [FacilitatorRoutes.FACILITATE_REFLECTION_NEW]: 'facilitate-reflection',
  [FacilitatorRoutes.QA]: 'qa',
  [FacilitatorRoutes.PROFILE]: 'profile',
  [FacilitatorRoutes.PERMISSIONS_INSTRUCTIONS]: 'permissions-instructions',
  [FacilitatorRoutes.USERS]: 'users', // Maps to SidebarSection.USERS
  [FacilitatorRoutes.AI_COACH]: 'ai-coach',
  // Dev tools routes
  [FacilitatorRoutes.DEV_TOOLS]: 'dev-tools',
  [FacilitatorRoutes.DEV_TOOLS_VOICE_LOTTIE]: 'dev-tools-voice-lottie',
  [FacilitatorRoutes.DEV_TOOLS_BUBBLE_LOTTIE]: 'dev-tools-bubble-lottie',
  [FacilitatorRoutes.DEV_TOOLS_VOICE_ANIMATION_AUDIO]: 'dev-tools-voice-animation-audio',
  [FacilitatorRoutes.DEV_TOOLS_RECHARTS_GLASS]: 'dev-tools-recharts-glass',
  [FacilitatorRoutes.DEV_TOOLS_COMMUNICATION_FEELING]: 'dev-tools-communication-feeling',
  [FacilitatorRoutes.DEV_TOOLS_STACKED_CHARTS]: 'dev-tools-stacked-charts',
  [FacilitatorRoutes.DEV_TOOLS_COMPARISON_BAR]: 'dev-tools-comparison-bar',
  [FacilitatorRoutes.DEV_TOOLS_TEAM_HEALTH_HEART]: 'dev-tools-team-health-heart',
  [FacilitatorRoutes.DEV_TOOLS_GLINT_REVEAL]: 'dev-tools-glint-reveal',
  [FacilitatorRoutes.DEV_TOOLS_GENERATE_REPORT_VISUALS]: 'dev-tools-generate-report-visuals',
  [FacilitatorRoutes.DEV_TOOLS_FUNNEL_GRAPH]: 'dev-tools-funnel-graph',
  [FacilitatorRoutes.DEV_TOOLS_FUNNEL_GRAPH_V2]: 'dev-tools-funnel-graph-v2',
  [FacilitatorRoutes.DEV_TOOLS_FUNNEL_GRAPH_V3]: 'dev-tools-funnel-graph-v3',
  [FacilitatorRoutes.DEV_TOOLS_TEAM_INFORMATION_SHARES]: 'dev-tools-team-information-shares',
  [FacilitatorRoutes.DEV_TOOLS_ACHIEVEMENTS]: 'dev-tools-achievements',
  [FacilitatorRoutes.DEV_TOOLS_BO_CLEANUP]: 'dev-tools-bo-cleanup',
  [FacilitatorRoutes.ORGANIZATION_MEMBERS]: 'organization-members',
  [FacilitatorRoutes.ORGANIZATION_INVITES]: 'organization-invites',
  [FacilitatorRoutes.ORGANIZATION_SETTINGS]: 'organization-settings'
}

/**
 * Shared route paths mapped to their section identifiers
 */
export const SHARED_ROUTE_TO_SECTION: Record<string, string> = {
  [SharedRoutes.GAME_CENTER]: 'game-center',
  [SharedRoutes.PLAY_GAME_WITH_INTRODUCTION]: 'play-game-with-introduction',
  [SharedRoutes.PLAY_GAME_WITHOUT_INTRODUCTION]: 'play-game-without-introduction'
}

/**
 * Get section identifier from full path
 */
export const getSectionFromPath = (path: string, role: 'gamer' | 'facilitator'): string | null => {
  // Remove trailing slash
  const cleanPath = path.replace(/\/$/, '')

  // Check role-specific routes first
  if (role === 'gamer') {
    const section = GAMER_ROUTE_TO_SECTION[cleanPath]
    if (section) return section
  } else {
    const section = FACILITATOR_ROUTE_TO_SECTION[cleanPath]
    if (section) return section
  }

  // Check shared routes - sort by route length (longest first) to match most specific routes first
  const sortedSharedRoutes = Object.entries(SHARED_ROUTE_TO_SECTION).sort((a, b) => b[0].length - a[0].length)

  for (const [route, section] of sortedSharedRoutes) {
    if (cleanPath.includes(route)) {
      return section
    }
  }

  // Handle special cases like activity-details, sprint details with params, back-office sub-routes, deeper analysis, dev-tools
  if (cleanPath.includes('/activity-details')) return 'activity-details'
  if (cleanPath.includes('/sprint/')) return 'sprint-details'
  if (cleanPath.includes('/ai-coach/')) return 'ai-coach'
  if (cleanPath.includes('/back-office')) return 'users' // Back office routes map to USERS section
  if (cleanPath.includes('/generate-report')) return 'generate-report'
  if (cleanPath.includes('/manage-reports')) return 'manage-reports'
  if (cleanPath.includes('/facilitate-reflection')) return 'facilitate-reflection'
  if (cleanPath.includes('/manage-reflections')) return 'manage-reflections'
  if (
    cleanPath.includes(FacilitatorRoutes.ORGANIZATION_MEMBERS) ||
    cleanPath.includes(FacilitatorRoutes.ORGANIZATION_INVITES)
  ) {
    return 'organization-members'
  }
  if (cleanPath.includes(FacilitatorRoutes.ORGANIZATION_SETTINGS)) return 'organization-settings'
  if (cleanPath.includes('/deeper-analysis/team')) return 'deeper-team-analysis'
  if (cleanPath.includes('/deeper-analysis/individual')) return 'deeper-individual-analysis'
  if (cleanPath.includes('/tutorial-videos')) return 'tutorial-videos'
  if (cleanPath.includes('/unseen-achievements') && !cleanPath.includes('/dev-tools')) return 'dashboard-gamer'
  if (cleanPath.includes('/dev-tools')) {
    // Dev-tools routes are now role-specific, check which role context we're in
    if (role === 'gamer') {
      // Check gamer dev-tools routes
      if (cleanPath.includes('/dev-tools/voice-lottie')) return 'dev-tools-voice-lottie'
      if (cleanPath.includes('/dev-tools/bubble-lottie')) return 'dev-tools-bubble-lottie'
      if (cleanPath.includes('/dev-tools/voice-animation-audio')) return 'dev-tools-voice-animation-audio'
      if (cleanPath.includes('/dev-tools/recharts-glass')) return 'dev-tools-recharts-glass'
      if (cleanPath.includes('/dev-tools/communication-feeling')) return 'dev-tools-communication-feeling'
      if (cleanPath.includes('/dev-tools/stacked-charts')) return 'dev-tools-stacked-charts'
      if (cleanPath.includes('/dev-tools/comparison-bar')) return 'dev-tools-comparison-bar'
      if (cleanPath.includes('/dev-tools/team-health-heart')) return 'dev-tools-team-health-heart'
      if (cleanPath.includes('/dev-tools/glint-reveal')) return 'dev-tools-glint-reveal'
      if (cleanPath.includes('/dev-tools/generate-report-visuals')) return 'dev-tools-generate-report-visuals'
      if (cleanPath.includes('/dev-tools/funnel-graph-v3')) return 'dev-tools-funnel-graph-v3'
      if (cleanPath.includes('/dev-tools/funnel-graph-v2')) return 'dev-tools-funnel-graph-v2'
      if (cleanPath.includes('/dev-tools/funnel-graph')) return 'dev-tools-funnel-graph'
      if (cleanPath.includes('/dev-tools/team-information-shares')) return 'dev-tools-team-information-shares'
      if (cleanPath.includes('/dev-tools/achievements')) return 'dev-tools-achievements'
      if (cleanPath.includes('/dev-tools/personality-traits')) return 'dev-tools-personality-traits'
      if (cleanPath.includes('/dev-tools/bo-cleanup')) return 'dev-tools-bo-cleanup'
      return 'dev-tools'
    } else {
      // Check facilitator dev-tools routes
      if (cleanPath.includes('/dev-tools/voice-lottie')) return 'dev-tools-voice-lottie'
      if (cleanPath.includes('/dev-tools/bubble-lottie')) return 'dev-tools-bubble-lottie'
      if (cleanPath.includes('/dev-tools/voice-animation-audio')) return 'dev-tools-voice-animation-audio'
      if (cleanPath.includes('/dev-tools/recharts-glass')) return 'dev-tools-recharts-glass'
      if (cleanPath.includes('/dev-tools/communication-feeling')) return 'dev-tools-communication-feeling'
      if (cleanPath.includes('/dev-tools/stacked-charts')) return 'dev-tools-stacked-charts'
      if (cleanPath.includes('/dev-tools/comparison-bar')) return 'dev-tools-comparison-bar'
      if (cleanPath.includes('/dev-tools/team-health-heart')) return 'dev-tools-team-health-heart'
      if (cleanPath.includes('/dev-tools/glint-reveal')) return 'dev-tools-glint-reveal'
      if (cleanPath.includes('/dev-tools/generate-report-visuals')) return 'dev-tools-generate-report-visuals'
      if (cleanPath.includes('/dev-tools/funnel-graph-v3')) return 'dev-tools-funnel-graph-v3'
      if (cleanPath.includes('/dev-tools/funnel-graph-v2')) return 'dev-tools-funnel-graph-v2'
      if (cleanPath.includes('/dev-tools/funnel-graph')) return 'dev-tools-funnel-graph'
      if (cleanPath.includes('/dev-tools/team-information-shares')) return 'dev-tools-team-information-shares'
      if (cleanPath.includes('/dev-tools/achievements')) return 'dev-tools-achievements'
      if (cleanPath.includes('/dev-tools/bo-cleanup')) return 'dev-tools-bo-cleanup'

      return 'dev-tools'
    }
  }

  return null
}

/**
 * Check if a route is a shared route
 */
export const isSharedRoute = (path: string): boolean => {
  return Object.values(SharedRoutes).some((route) => path.includes(route))
}

/**
 * Comprehensive route configuration map
 * Maps all routes to their metadata including route type and allowed roles
 */
const ROUTE_CONFIG_MAP: Record<string, RouteMetadata> = {
  // Public routes
  [PublicRoutes.HOME]: {
    path: PublicRoutes.HOME,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },
  [PublicRoutes.LOGIN]: {
    path: PublicRoutes.LOGIN,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },
  [PublicRoutes.SIGNED_IN]: {
    path: PublicRoutes.SIGNED_IN,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },
  [PublicRoutes.ONBOARDING]: {
    path: PublicRoutes.ONBOARDING,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },
  [PublicRoutes.TERMS]: {
    path: PublicRoutes.TERMS,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },
  [PublicRoutes.PRIVACY]: {
    path: PublicRoutes.PRIVACY,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },
  [PublicRoutes.ABOUT]: {
    path: PublicRoutes.ABOUT,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },
  [PublicRoutes.KNOWLEDGE_HUB]: {
    path: PublicRoutes.KNOWLEDGE_HUB,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },
  [PublicRoutes.BLOG_POSTS]: {
    path: PublicRoutes.BLOG_POSTS,
    authRequired: false,
    routeType: 'public',
    allowedRoles: undefined
  },

  // Shared routes (logged in - both gamer and facilitator)
  [`${GamerRoutes.BASE}${SharedRoutes.GAME_CENTER}`]: {
    path: `${GamerRoutes.BASE}${SharedRoutes.GAME_CENTER}`,
    authRequired: true,
    routeType: 'shared',
    allowedRoles: SHARED_ROLES
  },
  [`${FacilitatorRoutes.BASE}${SharedRoutes.GAME_CENTER}`]: {
    path: `${FacilitatorRoutes.BASE}${SharedRoutes.GAME_CENTER}`,
    authRequired: true,
    routeType: 'shared',
    allowedRoles: SHARED_ROLES
  },
  [`${GamerRoutes.BASE}${SharedRoutes.PLAY_GAME_WITH_INTRODUCTION}`]: {
    path: `${GamerRoutes.BASE}${SharedRoutes.PLAY_GAME_WITH_INTRODUCTION}`,
    authRequired: true,
    routeType: 'shared',
    allowedRoles: SHARED_ROLES
  },
  [`${FacilitatorRoutes.BASE}${SharedRoutes.PLAY_GAME_WITH_INTRODUCTION}`]: {
    path: `${FacilitatorRoutes.BASE}${SharedRoutes.PLAY_GAME_WITH_INTRODUCTION}`,
    authRequired: true,
    routeType: 'shared',
    allowedRoles: SHARED_ROLES
  },
  [`${GamerRoutes.BASE}${SharedRoutes.PLAY_GAME_WITHOUT_INTRODUCTION}`]: {
    path: `${GamerRoutes.BASE}${SharedRoutes.PLAY_GAME_WITHOUT_INTRODUCTION}`,
    authRequired: true,
    routeType: 'shared',
    allowedRoles: SHARED_ROLES
  },
  [`${FacilitatorRoutes.BASE}${SharedRoutes.PLAY_GAME_WITHOUT_INTRODUCTION}`]: {
    path: `${FacilitatorRoutes.BASE}${SharedRoutes.PLAY_GAME_WITHOUT_INTRODUCTION}`,
    authRequired: true,
    routeType: 'shared',
    allowedRoles: SHARED_ROLES
  },

  // Gamer routes
  [GamerRoutes.BASE]: {
    path: GamerRoutes.BASE,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.DASHBOARD]: {
    path: GamerRoutes.DASHBOARD,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: GAMER_ONLY_ROLES
  },
  [GamerRoutes.MY_PEOPLE]: {
    path: GamerRoutes.MY_PEOPLE,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: LEADER_ONLY_ROLES
  },
  [GamerRoutes.LATEST_SESSION_FEEDBACK]: {
    path: GamerRoutes.LATEST_SESSION_FEEDBACK,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.UPCOMING_ACTIVITIES]: {
    path: GamerRoutes.UPCOMING_ACTIVITIES,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.ONBOARDING]: {
    path: GamerRoutes.ONBOARDING,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.MY_PROFILE]: {
    path: GamerRoutes.MY_PROFILE,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.ACCOUNT_SETTINGS]: {
    path: GamerRoutes.ACCOUNT_SETTINGS,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.FAQ]: {
    path: GamerRoutes.FAQ,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.TUTORIAL_VIDEOS]: {
    path: GamerRoutes.TUTORIAL_VIDEOS,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.UNSEEN_ACHIEVEMENTS]: {
    path: GamerRoutes.UNSEEN_ACHIEVEMENTS,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: PARTICIPANT_ROLES
  },
  [GamerRoutes.DEV_TOOLS]: {
    path: GamerRoutes.DEV_TOOLS,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: GAMER_DEV_TOOLS_TEST_ROLES
  },

  // Facilitator routes
  [FacilitatorRoutes.BASE]: {
    path: FacilitatorRoutes.BASE,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.DASHBOARD]: {
    path: FacilitatorRoutes.DASHBOARD,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.TRAINING_SPRINT_MANAGEMENT]: {
    path: FacilitatorRoutes.TRAINING_SPRINT_MANAGEMENT,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.SPRINT_CREATION]: {
    path: FacilitatorRoutes.SPRINT_CREATION,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.SESSIONS]: {
    path: FacilitatorRoutes.SESSIONS,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.MANAGE_REPORTS]: {
    path: FacilitatorRoutes.MANAGE_REPORTS,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.GENERATE_REPORT]: {
    path: FacilitatorRoutes.GENERATE_REPORT,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.MANAGE_REFLECTIONS]: {
    path: FacilitatorRoutes.MANAGE_REFLECTIONS,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.FACILITATE_REFLECTION]: {
    path: FacilitatorRoutes.FACILITATE_REFLECTION,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.FACILITATE_REFLECTION_NEW]: {
    path: FacilitatorRoutes.FACILITATE_REFLECTION_NEW,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.QA]: {
    path: FacilitatorRoutes.QA,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.PROFILE]: {
    path: FacilitatorRoutes.PROFILE,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.USERS]: {
    path: FacilitatorRoutes.USERS,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.DEV_TOOLS]: {
    path: FacilitatorRoutes.DEV_TOOLS,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [GamerRoutes.DEV_TOOLS_FUNNEL_GRAPH_V2]: {
    path: GamerRoutes.DEV_TOOLS_FUNNEL_GRAPH_V2,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: GAMER_DEV_TOOLS_TEST_ROLES
  },
  [GamerRoutes.DEV_TOOLS_FUNNEL_GRAPH_V3]: {
    path: GamerRoutes.DEV_TOOLS_FUNNEL_GRAPH_V3,
    authRequired: true,
    routeType: 'gamer',
    allowedRoles: GAMER_DEV_TOOLS_TEST_ROLES
  },
  [FacilitatorRoutes.DEV_TOOLS_FUNNEL_GRAPH_V2]: {
    path: FacilitatorRoutes.DEV_TOOLS_FUNNEL_GRAPH_V2,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  },
  [FacilitatorRoutes.DEV_TOOLS_FUNNEL_GRAPH_V3]: {
    path: FacilitatorRoutes.DEV_TOOLS_FUNNEL_GRAPH_V3,
    authRequired: true,
    routeType: 'facilitator',
    allowedRoles: FACILITATOR_ROLES
  }
}

/**
 * Get route metadata for a given path
 */
export const getRouteMetadata = (path: string): RouteMetadata | null => {
  // Normalize path: remove trailing slash, but keep '/' as '/'
  const cleanPath = path === '/' ? '/' : path.replace(/\/$/, '')

  // Try exact match first
  if (ROUTE_CONFIG_MAP[cleanPath]) {
    return ROUTE_CONFIG_MAP[cleanPath]
  }

  // Try matching by prefix for dynamic routes and sub-routes
  for (const [routePath, metadata] of Object.entries(ROUTE_CONFIG_MAP)) {
    if (cleanPath.startsWith(routePath) || routePath.includes(cleanPath)) {
      // For dynamic routes like /sprint/:sprintId, check if pattern matches
      if (routePath.includes(':')) {
        const pattern = routePath.replace(/:[^/]+/g, '[^/]+')
        const regex = new RegExp(`^${pattern}$`)
        if (regex.test(cleanPath)) {
          return metadata
        }
      } else if (cleanPath.startsWith(routePath)) {
        return metadata
      }
    }
  }

  // Check if it's a gamer route
  if (cleanPath.startsWith(GamerRoutes.BASE)) {
    return {
      path: cleanPath,
      authRequired: true,
      routeType: 'gamer',
      allowedRoles: cleanPath.startsWith(GamerRoutes.DEV_TOOLS) ? GAMER_DEV_TOOLS_TEST_ROLES : PARTICIPANT_ROLES
    }
  }

  // Check if it's a facilitator route
  if (cleanPath.startsWith(FacilitatorRoutes.BASE)) {
    return {
      path: cleanPath,
      authRequired: true,
      routeType: 'facilitator',
      allowedRoles: FACILITATOR_ROLES
    }
  }

  // Check if it's a shared route
  if (isSharedRoute(cleanPath)) {
    return {
      path: cleanPath,
      authRequired: true,
      routeType: 'shared',
      allowedRoles: SHARED_ROLES
    }
  }

  return null
}

/**
 * Check if a route is allowed for a given user role
 */
export const isRouteAllowed = (path: string, role: OrgRole): boolean => {
  const metadata = getRouteMetadata(path)
  if (!metadata) return false

  // Public routes are always allowed
  if (metadata.routeType === 'public' && !metadata.authRequired) {
    return true
  }

  // If no allowed roles specified, allow if authenticated
  if (!metadata.allowedRoles || metadata.allowedRoles.length === 0) {
    return true
  }

  return metadata.allowedRoles.includes(role)
}

/**
 * Get route type from path
 */
export const getRouteType = (path: string): RouteType | null => {
  const metadata = getRouteMetadata(path)
  return metadata?.routeType || null
}

/** The Unity game pages of either shell: the DOM there sits behind the game's own fullscreen canvas. */
export const isGamePagePath = (pathname: string): boolean =>
  pathname.includes(SharedRoutes.PLAY_GAME_WITH_INTRODUCTION) ||
  pathname.includes(SharedRoutes.PLAY_GAME_WITHOUT_INTRODUCTION)

/** The gamer's AI debrief page, where the team's debrief runs with its own call. */
export const isAIDebriefPagePath = (pathname: string): boolean => pathname.startsWith(GamerRoutes.AI_COACH)

/** The page of the call a whole sprint holds an introduction or a group reflection in. */
export const buildSprintCallPath = ({ sprintId, activityId }: { sprintId: string; activityId: string }): string => {
  const query = new URLSearchParams({
    [SprintCallQueryParam.SPRINT_ID]: sprintId,
    [SprintCallQueryParam.ACTIVITY_ID]: activityId
  })

  return `${GamerRoutes.SPRINT_CALL}?${query.toString()}`
}

/** The gamer's Upcoming activities page itself, which carries the lobby buttons; the full list below it does not. */
export const isGamerUpcomingActivitiesPagePath = (pathname: string): boolean =>
  pathname.replace(/\/$/, '') === GamerRoutes.UPCOMING_ACTIVITIES
